import { FC, useState } from 'react'
import { Button } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import FormErrorsModal from '../../../../components/FormErrorsModal'
import SuccessModal from '../../../../components/SuccessModal'
import Loading from '../../../../components/Loading'
import NotificationDialog from '../../ReadEditOrder/components/NotificationDialog'
import { IEditOrderItemProps } from './editOrder/types'
import { useEditOrderData } from './editOrder/useEditOrderData'
import { useFileManagement } from './editOrder/useFileManagement'
import { useEditOrderForm } from './editOrder/useEditOrderForm'
import { StyledContainer, ActionButtons } from './editOrder/styledComponents'
import CustomerDetailsSection from './editOrder/CustomerDetailsSection'
import JobDetailsSection from './editOrder/JobDetailsSection'
import CommentsSection from './editOrder/CommentsSection'
import BudgetSection from './editOrder/BudgetSection'
import JobFileSection from './editOrder/JobFileSection'
import { PreviewsSection, ImageAssetsSection, VideoAssetsSection } from './editOrder/FileSections'

const EditOrderItem: FC<IEditOrderItemProps> = ({ order, onCancel, onSave }) => {
  const [notificationModalOpen, setNotificationModalOpen] = useState(false)
  const {
    assignees,
    customers,
    campaigns,
    originalMetaCampaigns,
    tiktokCampaigns,
    googleCampaigns,
    jobs,
    loadingAssignees,
    loadingCustomerNames,
    loadingCampaigns,
    loadingTiktokCampaigns,
    loadingGoogleCampaigns,
    loadingJobs,
    permission,
    isCustomer,
  } = useEditOrderData()

  const files = useFileManagement(order)

  const {
    formik,
    handleSubmit,
    loadingMutation,
    successModalOpen,
    setSuccessModalOpen,
    formikErrors,
    errorsModalOpen,
    setErrorsModalOpen,
  } = useEditOrderForm({
    order,
    assignees,
    customers,
    originalMetaCampaigns,
    tiktokCampaigns,
    googleCampaigns,
    jobs,
    isCustomer,
    headerImageId: files.headerImageId,
    previewFiles: files.previewFiles,
    assetsFiles: files.assetsFiles,
    videoAssets: files.videoAssets,
    previewIdsToDelete: files.previewIdsToDelete,
    assetsIdsToDelete: files.assetsIdsToDelete,
    videoAssetsIdsToDelete: files.videoAssetsIdsToDelete,
    jobFile: files.jobFile,
    existingJobFile: files.existingJobFile,
    deletePreviewImage: files.deletePreviewImage,
    deletePreviewAudio: files.deletePreviewAudio,
    deletePreviewVideo: files.deletePreviewVideo,
    deleteAssetImage: files.deleteAssetImage,
    deleteFileAssetFile: files.deleteFileAssetFile,
    onSave,
  })

  if (loadingMutation || loadingAssignees || loadingCustomerNames) {
    return <Loading />
  }

  const shouldAskNotification = order.status !== formik.values.status

  return (
    <StyledContainer>
      <form onSubmit={handleSubmit}>
        <CustomerDetailsSection
          formik={formik}
          order={order}
          customers={customers}
          assignees={assignees}
          isCustomer={isCustomer}
          permission={permission}
        />

        <JobDetailsSection
          formik={formik}
          order={order}
          campaigns={campaigns}
          originalMetaCampaigns={originalMetaCampaigns}
          tiktokCampaigns={tiktokCampaigns}
          googleCampaigns={googleCampaigns}
          jobs={jobs}
          loadingCampaigns={loadingCampaigns}
          loadingTiktokCampaigns={loadingTiktokCampaigns}
          loadingGoogleCampaigns={loadingGoogleCampaigns}
          loadingJobs={loadingJobs}
          isCustomer={isCustomer}
          permission={permission}
        />

        <CommentsSection formik={formik} order={order} permission={permission} />

        <BudgetSection
          formik={formik}
          order={order}
          isCustomer={isCustomer}
          permission={permission}
        />

        <JobFileSection
          jobFile={files.jobFile}
          existingJobFile={files.existingJobFile}
          setExistingJobFile={files.setExistingJobFile}
          jobAdLink={formik.values.jobAdLink}
          getRootProps={files.getRootPropsJobFile}
          getInputProps={files.getInputPropsJobFile}
          acceptedFiles={files.acceptedJobFiles}
          isDragActive={files.isDragActiveJobFile}
          deleteJobFileHandler={files.deleteJobFileHandler}
        />

        <PreviewsSection
          orderId={order?.id}
          previewFiles={files.previewFiles}
          setPreviewFiles={files.setPreviewFiles}
          previewsDataSources={files.previewsDataSources}
          setPreviewsDataSources={files.setPreviewsDataSources}
          previewIdsToDelete={files.previewIdsToDelete}
          setPreviewIdsToDelete={files.setPreviewIdsToDelete}
          previewIdsToCreate={files.previewIdsToCreate}
          setPreviewIdsToCreate={files.setPreviewIdsToCreate}
          formik={formik}
          permission={permission}
          isCustomer={isCustomer}
        />

        <ImageAssetsSection
          assetsFiles={files.assetsFiles}
          setAssetsFiles={files.setAssetsFiles}
          assetsDataSources={files.assetsDataSources}
          setAssetsDataSources={files.setAssetsDataSources}
          assetsIdsToDelete={files.assetsIdsToDelete}
          setAssetsIdsToDelete={files.setAssetsIdsToDelete}
          assetsIdsToCreate={files.assetsIdsToCreate}
          setAssetsIdsToCreate={files.setAssetsIdsToCreate}
          headerImageId={files.headerImageId}
          handleToggleHeaderImage={files.handleToggleHeaderImage}
          permission={permission}
        />

        <VideoAssetsSection
          videoAssets={files.videoAssets}
          setVideoAssets={files.setVideoAssets}
          videoAssetsDataSources={files.videoAssetsDataSources}
          setVideoAssetsDataSources={files.setVideoAssetsDataSources}
          videoAssetsIdsToDelete={files.videoAssetsIdsToDelete}
          setVideoAssetsIdsToDelete={files.setVideoAssetsIdsToDelete}
          videoAssetsIdsToCreate={files.videoAssetsIdsToCreate}
          setVideoAssetsIdsToCreate={files.setVideoAssetsIdsToCreate}
          permission={permission}
        />

        <ActionButtons>
          <Button variant="outlined" size="large" onClick={onCancel}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            type={shouldAskNotification ? 'button' : 'submit'}
            onClick={() => (shouldAskNotification ? setNotificationModalOpen(true) : null)}
            disabled={loadingMutation}
          >
            <FormattedMessage id="save" />
          </Button>
        </ActionButtons>

        <FormErrorsModal
          errorsModalOpen={errorsModalOpen}
          formikErrors={formikErrors}
          setErrorsModalOpen={setErrorsModalOpen}
        />
        <SuccessModal
          successModalOpen={successModalOpen}
          handleClose={() => {
            setSuccessModalOpen(false)
            onCancel()
          }}
          setSuccessModalOpen={setSuccessModalOpen}
          message={<FormattedMessage id="edited-successfully" />}
        />
      </form>

      <NotificationDialog
        setFieldValue={formik.setFieldValue}
        submit={formik.handleSubmit}
        open={notificationModalOpen}
        setOpen={setNotificationModalOpen}
      />
    </StyledContainer>
  )
}

export default EditOrderItem
